import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { hasJWT, useAPIWrapper } from 'drf-react-by-schema';
import useTheme from '@mui/system/useTheme';
import useMediaQuery from '@mui/system/useMediaQuery';

import { appDispatch, getPageInfo } from 'src/utils/utils';
import { setPage } from 'src/features/pageSlice';
import { useAppSelector } from 'src/app/hooks';
import { useFormSubmit } from 'src/hooks/useFormSubmit';
import { PageType } from 'src/constants/PageType';
import { PageBasics, PerfilType } from 'src/@types';
import PAGES from 'src/pageTree';
import { GENERAL_ADMINS } from 'src/constants/Permission';

import { Main, MainInner, MainInnerDesktopContent, MainWrapper } from 'src/styles/styledComponents';
import SidebarMenu from './SidebarMenu';
import TopBar from './TopBar';
import BottomBar from './BottomBar';
import TopBarView from './TopBarView';
import TopBarForm from './TopBarForm';
import FormButtons from './FormButtons';
import BottomBarForm from './BottomBarForm';
import { useHasPermissionsMulti } from 'src/hooks/useHasPermissions';

export default function AuthRoute() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { key, type, handleSubmit, perfil } = useAppSelector((state) => state.page);
  const { usuaria: perfilFromAPI, setPageForm } = useAPIWrapper();
  const { onLocalSubmit } = useFormSubmit();
  const [URLSearchParams] = useSearchParams();

  const pageId = useMemo(() => {
    return params?.objId;
  }, [params?.objId]);
  const { pageKey, pageType, pageTitle } = useMemo(() => {
    return getPageInfo(location, pageId);
  }, [location, pageId]);

  const [hasPageAccess, isAdmin] = useHasPermissionsMulti([pageKey, GENERAL_ADMINS]);

  const page = parseInt(URLSearchParams.get('page') || '1');

  const [open, setOpen] = useState(false);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!perfil) {
      return;
    }

    if (pageKey === 'perfil' && (!pageId || (!isAdmin && parseInt(pageId) !== perfil.id))) {
      navigate(`/perfil/view/${perfil.id}`);
      return;
    }

    if (
      pageKey === 'entreposto' &&
      (!pageId || (!isAdmin && (!perfil.entreposto || parseInt(pageId) !== perfil.entreposto.id)))
    ) {
      navigate(perfil.entreposto ? `/entreposto/view/${perfil.entreposto.id}` : '/');
      return;
    }

    if (perfil.first_run && pageKey !== 'perfil') {
      navigate(`/perfil/edit/${perfil.id}`);
      return;
    }

    const PAGE = PAGES[pageKey || 'home'];

    if (PAGE && !hasPageAccess) {
      console.log('Access not allowed.');
      navigate('/');
      return;
    }

    document.title = pageTitle;
    const newPage: Partial<PageBasics> = {
      id: pageId,
      key: pageKey,
      type: pageType,
      title: pageTitle,
      subTitle: null,
      isMobile,
    };
    if (pageType && ![PageType.CREATE, PageType.EDIT].includes(pageType)) {
      newPage.formWizard = null;
    }
    appDispatch(setPage(newPage));
  }, [pageKey, pageType, pageId, pageTitle, isMobile, perfil, hasPageAccess, isAdmin]);

  useEffect(() => {
    if (perfilFromAPI) {
      if (!hasJWT() || perfilFromAPI.erro === 'token inválido') {
        navigate('/login');
      } else {
        appDispatch(
          setPage({
            usuaria: perfilFromAPI.usuaria,
            perfil: perfilFromAPI as PerfilType | null,
          })
        );
      }
    }
  }, [perfilFromAPI]);

  useEffect(() => {
    setPageForm({
      initialValues: null,
      schema: null,
    });
  }, [location, setPageForm]);

  if (!perfil) {
    return null;
  }

  /////////
  // EXISTING VIEWS ARE: FORM (CREATE/EDIT), DETAIL AND LIST VIEWS
  /////////

  if (isMobile && type && [PageType.CREATE, PageType.EDIT, PageType.VIEW].includes(type)) {
    if (type === PageType.VIEW) {
      // MOBILE: DETAIL VIEW
      return (
        <MainWrapper>
          <TopBarView />
          <Main isMobile={true}>
            <MainInner isForm={true}>
              <Outlet />
            </MainInner>
          </Main>
        </MainWrapper>
      );
    }

    if (!handleSubmit) {
      return null;
    }

    // MOBILE: FORM VIEW
    return (
      <MainWrapper>
        <form onSubmit={handleSubmit(onLocalSubmit)}>
          <TopBarForm page={page} />
          <Main isMobile={true}>
            <MainInner isForm={true}>
              <Outlet />
            </MainInner>
          </Main>
          <BottomBarForm page={page} />
        </form>
      </MainWrapper>
    );
  }

  // DESKTOP: FORM VIEW
  if (type && [PageType.CREATE, PageType.EDIT].includes(type)) {
    if (!handleSubmit) {
      return null;
    }
    return (
      <MainWrapper>
        {/* <TopBar /> */}
        <Main isFilledBackground={true} isMobile={false}>
          <MainInner maxWidth="md">
            <MainInnerDesktopContent>
              <form onSubmit={handleSubmit(onLocalSubmit)}>
                <FormButtons />
                <Outlet />
                <FormButtons bottom={true} />
              </form>
            </MainInnerDesktopContent>
          </MainInner>
        </Main>
      </MainWrapper>
    );
  }

  const simpleOnePage = ['', 'contato', 'sobre'].includes(key || '');
  const isFilledBackground = key !== null && (simpleOnePage || !isMobile);
  const maxWidth = simpleOnePage || type === PageType.LIST ? 'xl' : 'md';

  // MOBILE: LIST VIEW
  // DESKTOP: DETAIL AND LIST VIEWS
  return (
    <>
      <MainWrapper>
        <TopBar handleDrawerToggle={handleDrawerToggle} />
        <Main isFilledBackground={isFilledBackground} isMobile={isMobile}>
          <MainInner maxWidth={maxWidth} isFilledBackground={isFilledBackground}>
            {isMobile || simpleOnePage ? (
              <Outlet />
            ) : (
              <MainInnerDesktopContent>
                <Outlet />
              </MainInnerDesktopContent>
            )}
          </MainInner>
        </Main>
      </MainWrapper>
      <BottomBar isMobile={isMobile} />
      <SidebarMenu
        handleDrawerClose={handleDrawerClose}
        handleDrawerToggle={handleDrawerToggle}
        open={open}
      />
    </>
  );
}
